import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { trackEvent } from "@/lib/analytics";


const brandCurve: [number, number, number, number] = [0.16, 1, 0.3, 1];

type ContactModalContextValue = {
  open: () => void;
  close: () => void;
  isOpen: boolean;
};

const ContactModalContext = createContext<ContactModalContextValue | null>(null);

export const useContactModal = () => {
  const ctx = useContext(ContactModalContext);
  if (!ctx) throw new Error("useContactModal must be used within ContactModalProvider");
  return ctx;
};

type FormState = {
  name: string;
  surname: string;
  title: "Mr." | "Ms." | "";
  company: string;
  email: string;
  phone: string;
  address1: string;
  address2: string;
  zip: string;
  city: string;
  country: string;
  reason: string;
  privacy: boolean;
};

const initialState: FormState = {
  name: "",
  surname: "",
  title: "",
  company: "",
  email: "",
  phone: "",
  address1: "",
  address2: "",
  zip: "",
  city: "",
  country: "",
  reason: "",
  privacy: false,
};

const REASON_OPTIONS = [
  "Get to know call",
  "Full platform demo",
  "Gamification demo",
  "Dashboard demo",
  "Other",
];

const REQUIRED: (keyof FormState)[] = [
  "name",
  "surname",
  "company",
  "email",
  "reason",
];

const labelCls = "block font-body text-[10px] uppercase tracking-[0.24em] text-neutral-400";
const inputCls =
  "w-full border-0 border-b border-white/20 bg-transparent px-0 py-2 font-body text-sm text-white placeholder-neutral-600 outline-none transition-colors focus:border-white";
const errorCls = "border-b-red-500/70 focus:border-red-400";

type ReasonDropdownProps = {
  value: string;
  onChange: (v: string) => void;
  error?: boolean;
};

const ReasonDropdown = ({ value, onChange, error }: ReasonDropdownProps) => {
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState<number>(-1);
  const rootRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  useEffect(() => {
    if (open) {
      const idx = REASON_OPTIONS.indexOf(value);
      setActiveIdx(idx >= 0 ? idx : 0);
    }
  }, [open, value]);

  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (!open) {
        setOpen(true);
      } else if (activeIdx >= 0) {
        onChange(REASON_OPTIONS[activeIdx]);
        setOpen(false);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) setOpen(true);
      else setActiveIdx((i) => Math.min(REASON_OPTIONS.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (!open) setOpen(true);
      else setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === "Tab") {
      setOpen(false);
    }
  };

  return (
    <div ref={rootRef} className="relative">
      <label className={labelCls} htmlFor="cf-reason">
        Reason of Contact*
      </label>
      <button
        id="cf-reason"
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={handleKey}
        className={`${inputCls} flex items-center justify-between pr-6 text-left ${value ? "" : "text-neutral-600"} ${error ? errorCls : ""}`}
      >
        <span>{value || "Select reason"}</span>
        <ChevronDown className={`h-3 w-3 text-neutral-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <ul
          ref={listRef}
          role="listbox"
          className="absolute left-0 right-0 top-full z-10 mt-1 border border-white/10 bg-[#050505] py-1 shadow-2xl"
        >
          {REASON_OPTIONS.map((opt, i) => {
            const selected = opt === value;
            const active = i === activeIdx;
            return (
              <li
                key={opt}
                role="option"
                aria-selected={selected}
                onMouseEnter={() => setActiveIdx(i)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  onChange(opt);
                  setOpen(false);
                }}
                className={`cursor-pointer px-4 py-2 font-body text-sm text-white transition-colors ${
                  selected ? "bg-[#333333]" : active ? "bg-[#2a2a2a]" : "bg-transparent"
                }`}
              >
                {opt}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

const ContactModal = () => {

  const { isOpen, close } = useContactModal();
  const [form, setForm] = useState<FormState>(initialState);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, boolean>>>({});
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setSubmitted(false);
      setErrors({});
      setForm(initialState);
      setSending(false);
      setSubmitError(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && close();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [isOpen, close]);

  const update = <K extends keyof FormState>(k: K, v: FormState[K]) => {
    setForm((p) => ({ ...p, [k]: v }));
    setErrors((p) => ({ ...p, [k]: false }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const next: Partial<Record<keyof FormState, boolean>> = {};
    REQUIRED.forEach((k) => {
      if (!String(form[k] ?? "").trim()) next[k] = true;
    });
    if (!form.privacy) next.privacy = true;
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) next.email = true;
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    setSending(true);
    setSubmitError(null);
    try {
      const { data, error } = await supabase.functions.invoke("send-contact-email", {
        body: {
          name: form.name,
          surname: form.surname,
          title: form.title,
          company: form.company,
          email: form.email,
          phone: form.phone,
          address1: form.address1,
          address2: form.address2,
          zip: form.zip,
          city: form.city,
          country: form.country,
          reason: form.reason,
          privacy: form.privacy,
        },
      });
      if (error || (data && (data as { error?: string }).error)) {
        throw new Error(error?.message ?? "send failed");
      }
      setSubmitted(true);
      void trackEvent("contact_form_submit", { category: "cta", section_name: "contact_modal" });
    } catch (err) {
      console.error(err);
      setSubmitError("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const Field = useMemo(
    () =>
      ({
        name,
        label,
        type = "text",
        required,
      }: {
        name: keyof FormState;
        label: string;
        type?: string;
        required?: boolean;
      }) => (
        <div>
          <label className={labelCls} htmlFor={`cf-${name}`}>
            {label}
            {required ? "*" : ""}
          </label>
          <input
            id={`cf-${name}`}
            type={type}
            value={form[name] as string}
            onChange={(e) => update(name, e.target.value as never)}
            className={`${inputCls} ${errors[name] ? errorCls : ""}`}
            autoComplete="off"
          />
        </div>
      ),
    [form, errors],
  );

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[10000] flex items-start justify-center overflow-y-auto bg-black/85 backdrop-blur-md"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25, ease: brandCurve }}
          onClick={close}
        >
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 16 }}
            transition={{ duration: 0.35, ease: brandCurve }}
            onClick={(e) => e.stopPropagation()}
            className="relative my-4 w-full max-w-5xl border border-white/10 bg-black p-4 pt-12 text-white max-h-[90vh] overflow-y-auto md:my-16 md:max-h-none md:overflow-visible md:p-0 md:px-16 md:py-14"
          >
            <button
              onClick={close}
              aria-label="Close"
              className="absolute right-5 top-5 flex h-10 w-10 items-center justify-center text-neutral-300 transition-colors hover:text-white"
            >
              <X className="h-5 w-5" />
            </button>

            {submitted ? (
              <div className="flex min-h-[40vh] flex-col items-center justify-center text-center">
                <div className="h-px w-12 bg-white/30" />
                <h2 className="mt-6 font-display text-3xl font-black uppercase tracking-tight md:text-5xl">
                  Thank you
                </h2>
                <p className="mt-4 max-w-md font-body text-sm leading-relaxed text-neutral-300 md:text-base">
                  We&rsquo;ll get in touch with you shortly.
                </p>
              </div>
            ) : (
              <>
                <div className="h-px w-12 bg-white/30" />
                <h2 className="mt-5 font-display text-3xl font-black uppercase tracking-tight md:text-5xl">
                  Get Started
                </h2>
                <p className="mt-4 max-w-xl font-body text-sm leading-relaxed text-neutral-300 md:text-base">
                  Want to get started with a free walkthrough? Enter your contact details and we will get in touch with you.
                </p>

                <form onSubmit={handleSubmit} className="mt-10 grid grid-cols-1 gap-x-12 gap-y-7 md:grid-cols-2">
                  <Field name="name" label="Name" required />
                  <Field name="surname" label="Surname" required />

                  <div>
                    <span className={labelCls}>Title</span>
                    <div className="mt-3 flex items-center gap-6">
                      {(["Mr.", "Ms."] as const).map((t) => (
                        <label key={t} className="flex cursor-pointer items-center gap-2 font-body text-sm text-white">
                          <input
                            type="radio"
                            name="title"
                            checked={form.title === t}
                            onChange={() => update("title", t)}
                            className="h-3 w-3 accent-white"
                          />
                          {t}
                        </label>
                      ))}
                    </div>
                  </div>
                  <Field name="company" label="Company" required />

                  <Field name="email" label="E-mail" type="email" required />
                  <Field name="phone" label="Phone" type="tel" />

                  <Field name="address1" label="Address Line 1" />
                  <Field name="address2" label="Address Line 2" />

                  <Field name="zip" label="ZIP Code" />
                  <Field name="city" label="City" />

                  <Field name="country" label="Country" />
                  <div>
                    <ReasonDropdown
                      value={form.reason}
                      onChange={(v) => update("reason", v)}
                      error={!!errors.reason}
                    />
                  </div>


                  <div className="md:col-span-1">
                    <label className="flex cursor-pointer items-start gap-3">
                      <input
                        type="checkbox"
                        checked={form.privacy}
                        onChange={(e) => update("privacy", e.target.checked)}
                        className={`mt-1 h-4 w-4 accent-white ${errors.privacy ? "outline outline-1 outline-red-500" : ""}`}
                      />
                      <span className="font-body text-xs leading-relaxed text-neutral-300">
                        I have read and hereby accept the{" "}
                        <a
                          href="https://fanpictor.com/privacy-policy.php"
                          target="_blank"
                          rel="noreferrer"
                          className="underline underline-offset-4 hover:text-white"
                        >
                          Privacy Policy
                        </a>
                        .
                      </span>
                    </label>
                  </div>

                  <div className="flex flex-col items-stretch justify-end gap-3 md:items-end">
                    {submitError && (
                      <p className="font-body text-xs text-red-400">{submitError}</p>
                    )}
                    <button
                      type="submit"
                      disabled={sending}
                      className="inline-flex w-full justify-center items-center gap-2 border border-white px-8 py-3 font-body text-xs uppercase tracking-[0.24em] text-white transition-colors hover:bg-white hover:text-black disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-transparent disabled:hover:text-white md:w-auto md:justify-start"
                    >
                      {sending ? "Sending…" : "Send"}
                    </button>
                  </div>

                  <p className="font-body text-[10px] uppercase tracking-[0.24em] text-neutral-500 md:col-span-2">
                    * Mandatory Fields
                  </p>
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
};

export const ContactModalProvider = ({ children }: { children: React.ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const value = useMemo(
    () => ({
      isOpen,
      open: () => {
        void trackEvent("get_in_touch_click", { category: "cta", section_name: "contact_modal" });
        setIsOpen(true);
      },
      close: () => setIsOpen(false),
    }),
    [isOpen],
  );
  return (
    <ContactModalContext.Provider value={value}>
      {children}
      <ContactModal />
    </ContactModalContext.Provider>
  );
};

export default ContactModal;
